import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Order } from '../../interfaces/orders/order';
import { Subject, takeUntil } from 'rxjs';
import { OrderManagerService } from '../../services/order-manager/order-manager.service';
import { BusinessSearchResultRepositoryService } from '../../services/business-search-results/business-search-result-repository.service';
import { BusinessSearchResult } from '../../interfaces/business-search-results/business-search-result';
import { ListComponent } from '../list-component';
import { animate, query, style, transition, trigger, sequence } from '@angular/animations';
import { OrderRepositoryService } from '../../services/order/order-repository-service.service';
import { Router } from '@angular/router';
import { eOrderStatusType, eOrderType, eResultState, eResultStateID } from '../../enums';
import { BusinessReportForBusinessSearchResult } from '../../interfaces/business-reports-for-business-search-result/business-search-result';
import { HttpParams } from '@angular/common/http';

@Component({
  selector: 'lib-us-business-search-results',
  templateUrl: './us-business-search-results.component.html',
  styleUrls: ['./us-business-search-results.component.css'],
  animations: [
    trigger('loadingToLoaded', [
      transition('* => Complete', [
        sequence([
          query('.spinner-animation', [
            style({ height: '*', opacity: 1 }),
            animate('300ms ease-in', style({ height: '0', opacity: 0 }))
          ], { optional: true }),
          query('.results-animation', [
            style({ height: '0', opacity: 0, overflow: 'hidden' }),
            animate('300ms ease-out', style({ height: '*', opacity: 1 }))
          ], { optional: true })
        ])
      ])
    ])
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class UsBusinessSearchResultsComponent extends ListComponent<BusinessSearchResult> implements OnInit, OnDestroy {
  @Input() showOrderReportButton: boolean = true;
  @Input() isAdmin: boolean = false;
  order!: Order;
  businessSearchResults!: BusinessSearchResult[];
  displayedColumns: string[] = ['name', 'address', 'type', 'orderReport'];
  protected isSaving: boolean = false;

  private unsubscribe$ = new Subject<void>();

  constructor(
    private orderManager: OrderManagerService,
    private ordersRepo: OrderRepositoryService,
    private router: Router,
    repo: BusinessSearchResultRepositoryService) {
    super(repo)
  }

  ngOnInit(): void {
    this.order = this.orderManager.currentOrder.value;
    this.orderManager.currentOrder
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(order => {
        this.order = order;
        this.repo.populateBaseLink([this.order.id]);
        let defaultParams = new HttpParams().set("SortColumn", "Score").set("SortDirection", "Asc").set("ShowResult", true);
        this.repo.getFirstPageSubscribable(defaultParams).subscribe();
      });

    if (this.isAdmin) {
      this.displayedColumns = this.displayedColumns.concat(['source', 'score']);
    }

    if (this.showOrderReportButton == false) {
      this.displayedColumns = this.displayedColumns.filter(column => column !== 'orderReport');
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  getFullAddress(result: BusinessSearchResult): string {
    return result.address?.streetName ?? '';
  }

  orderReport(result: BusinessSearchResult) {
    this.isSaving = true;

    const order = {
      referenceNumber: this.order.referenceNumber,
      country: this.order.country,
      jurisdiction: result.jurisdiction,
      orderTypeID: eOrderType.businessReport,
      orderGroupID: this.order.orderGroupID,
      businessSearchResultID: result.id,
      businessReportCriteria: {
        businessName: result.name,
        address: result.address?.streetName,
        ultimateBeneficialOwnershipShareholderID: this.order.businessSearchCriteria?.ultimateBeneficialOwnershipShareholderID
      }
    }

    this.ordersRepo.createNewOrder(order)
      .subscribe(result => {
        const originatingOrderID = this.order.id;
        this.isSaving = false;
        const order = (result as any).resource;
        this.order = order;
        this.orderManager.updateOrder(order);
        if (this.order.id) {
          this.router.navigate(['orders', 'business', order.id], {
            queryParams: {
              originatingOrderID: originatingOrderID,
              destination: "originating"
            }
          });
        }
      })
  }

  navigateToOrder(result: BusinessSearchResult) {
    const timeOrderedReports = result.businessReports?.sort((a, b) => {
      return new Date(b.added).getTime() - new Date(a.added).getTime();
    });

    const mostRecentReport = timeOrderedReports?.[0];

    if (mostRecentReport) {
      this.router.navigate(['/orders', mostRecentReport.businessReportOrderID]);
    }
  }

  resultsOverflow() {
    return this.order.searchResult?.resultState.id == eResultState.resultsOverflow ||
      this.order.searchResult?.resultState.id == eResultStateID.resultsOverflow;
  }

  noResults() {
    return this.order.searchResult?.resultState.id == eResultState.noResult ||
      this.order.searchResult?.resultState.id == eResultStateID.noResult;
  }

  shouldShowOrderReportButton(result: BusinessSearchResult): boolean {
    const reportList = (result.businessReports ?? []);
    return reportList.length === 0;
  }

  shouldShowViewReportButton(result: BusinessSearchResult): boolean {
    return (result.businessReports ?? []).length > 0;
  }

  shouldShowPendingReportButton(result: BusinessSearchResult): boolean {
    return this.filterPendingReports(result.businessReports).length > 0;
  }

  filterPendingReports(reports: BusinessReportForBusinessSearchResult[] | null | undefined) {
    return reports?.filter(report => report.businessReportOrderOrderStatusTypeID !== eOrderStatusType.draft
      && report.businessReportOrderOrderStatusTypeID !== eOrderStatusType.complete
      && report.businessReportOrderOrderStatusTypeID !== eOrderStatusType.cancelled) ?? [];
  }

  isReportAvailable(result: BusinessSearchResult): boolean {
    return result.isReportAvailable;
  }
}
